import React from 'react';
import { useForm } from '@formspree/react';
import { FieldValues } from 'react-hook-form';
import { FORMSPREE_TOKEN } from '../../../constants';
import FadeTransition from '../../animations/transitions/fade-transition';
import FormSuccess from '../../primitives/form-success';
import FormRegion from '../../primitives/form-region';
import FormFieldset from '../../primitives/form-fieldset';
import Input from '../../primitives/input';
import StyledButton from '../../primitives/styled-button';
import FormProvider from '../../primitives/form-provider';
import Upload from '../../primitives/upload';
import formatPhoneLink from '../../utils/format-phone-link';

interface FirstResponderFormProps {
    submitButton?: string
    successMessage?: string
}

const FirstResponderForm = ({ submitButton, successMessage }: FirstResponderFormProps): JSX.Element => {
    const [showSuccess, setShowSuccess] = React.useState(false);

    const [state, handleSubmit] = useForm(FORMSPREE_TOKEN);
    const [isLoading, setIsLoading] = React.useState(false);

    const handleFormSubmit = (data: FieldValues): void => {
        const formData = new FormData();
        Object.keys(data).forEach((key) => {
            formData.append(
                key,
                ['yourPhoneNumber', 'firstResponderPhoneNumber'].indexOf(key) >= 0
                    ? formatPhoneLink(data[key])
                    : data[key],
            );
        });
        handleSubmit(formData);
    };

    React.useEffect(() => {
        if (state.succeeded) {
            setIsLoading(false);
            setShowSuccess(true);
        } else if (state.submitting) {
            setIsLoading(true);
        } else if (state.errors) {
            // eslint-disable-next-line no-console
            console.error('Errors', state.errors);
            setIsLoading(false);
        }
    }, [state]);

    return (
        <FadeTransition shouldChange={showSuccess.toString()}>
            {!showSuccess && (
                <FormProvider
                    onSubmit={handleFormSubmit}
                    disabled={isLoading || showSuccess}
                >
                    <FormRegion description="Your Contact Information">
                        <FormFieldset name="Name">
                            <Input name="yourFirstName" placeholder="First Name" autoComplete="given-name" required />
                            <Input name="yourLastName" placeholder="Last Name" autoComplete="family-name" required />
                        </FormFieldset>
                        <FormFieldset name="Email">
                            <Input name="yourEmailAddress" placeholder="Email Address" autoComplete="email" required />
                        </FormFieldset>
                        <FormFieldset name="Phone">
                            <Input name="yourPhoneNumber" validationRegex={/^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g} type="tel" placeholder="Phone Number" autoComplete="tel" required />
                        </FormFieldset>

                    </FormRegion>
                    <FormRegion description="First Responder Contact Information">
                        <FormFieldset name="Name">
                            <Input name="firstResponderFirstName" placeholder="First Name" autoComplete="given-name" required />
                            <Input name="firstResponderLastName" placeholder="Last Name" autoComplete="family-name" required />
                        </FormFieldset>
                        <FormFieldset name="Email">
                            <Input name="firstResponderEmailAddress" placeholder="Email Address" autoComplete="email" required />
                        </FormFieldset>
                        <FormFieldset name="Phone">
                            <Input name="firstResponderPhoneNumber" validationRegex={/^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g} type="tel" placeholder="Phone Number" autoComplete="tel" required />
                        </FormFieldset>
                    </FormRegion>
                    <FormRegion description="Service Photo">
                        <FormFieldset name="">
                            <Upload name="firstResponderPhoto" placeholder="Upload a photo" />
                        </FormFieldset>
                    </FormRegion>
                    <FormRegion description="">
                        <FormFieldset name="">
                            <StyledButton type="submit" loading={isLoading}>
                                {submitButton || 'Submit'}
                            </StyledButton>
                        </FormFieldset>
                    </FormRegion>
                </FormProvider>
            )}
            {showSuccess && (
                <FormSuccess dangerouslySetInnerHTML={{ __html: successMessage || 'Submitted successfully.' }} />
            )}
        </FadeTransition>
    );
};

export default FirstResponderForm;
